import {IRouteDefinition, TRouteId} from "../index";
import {getRouteDefinition} from "../constants/routeLookup";
import {customStoreObservable} from "./sessionstore";
import {getEadmRole} from "./misc";
import {global} from "../appContext";
import {nsRoute} from "./route";
import {resolveShellTabIds} from "./pageShellTabRoutes";

/** Amplify / session key for a shell's last selected tab. */
export function getTabMemoryStoreId(shellDef: IRouteDefinition): string {
    return `${getTabMemoryKey(shellDef)}.current.tab`;
}

/** Canonical id used as the tab-memory store namespace. */
export function getTabMemoryKey(shellDef: IRouteDefinition): TRouteId {
    return shellDef.tabMemory ?? shellDef.id;
}

/** Persisted last-tab observable for a PageShell (or tabbed) route. */
export function getTabMemoryObservable(shellDef: IRouteDefinition): KnockoutObservable<TRouteId | null> {
    return customStoreObservable(getTabMemoryStoreId(shellDef), null);
}

/**
 * Permission + license check for a tab route before the shell mounts
 * (object-driven show() is applied later).
 */
export function isTabPermissibleEarly(tabDef: IRouteDefinition | null | undefined): boolean {
    if (!tabDef) {
        return false;
    }
    const org = global.login.selectedOrganization();
    if (!org) {
        return false;
    }
    if (!nsRoute.isRouteLicenseOkay(tabDef, org)) {
        return false;
    }
    const permissionLevel = tabDef.permissionLevel ?? 0;
    return getEadmRole() >= permissionLevel;
}

/** True when tabId is a configured shell tab and passes early permission/license checks. */
export function isShellTabAvailableEarly(shellDef: IRouteDefinition, tabId: TRouteId | null | undefined): boolean {
    if (!tabId) {
        return false;
    }
    const tabIds = resolveShellTabIds(shellDef);
    if (!tabIds.includes(tabId)) {
        return false;
    }
    return isTabPermissibleEarly(getRouteDefinition(tabId));
}

export interface IResolveShellTabIdOptions {
    /** Tab from URL / matched child route. */
    explicitTabId?: TRouteId | null;
    /** Last remembered tab for this memory key. */
    storedTabId?: TRouteId | null;
    /** Availability predicate (early or live Tab.isVisible). */
    isAvailable: (tabId: TRouteId) => boolean;
    /**
     * When false, if nothing passes isAvailable, still return tabDefault / first tab
     * so bare PageShell URLs can redirect (e.g. before login). Default true.
     */
    requireAvailable?: boolean;
}

/**
 * Pick the active tab for a PageShell.
 * Priority: available URL tab → available store → available tabDefault → first presented.
 * When requireAvailable is false, still returns tabDefault / first configured tab so bare
 * shell URLs can redirect before login/org is ready (RouteValidator handles access).
 */
export function resolveShellTabId(
    shellDef: IRouteDefinition,
    options: IResolveShellTabIdOptions,
): TRouteId | null {
    const {explicitTabId, storedTabId, isAvailable, requireAvailable = true} = options;
    if (explicitTabId && isAvailable(explicitTabId)) {
        return explicitTabId;
    }
    if (storedTabId && isAvailable(storedTabId)) {
        return storedTabId;
    }
    if (shellDef.tabDefault && isAvailable(shellDef.tabDefault)) {
        return shellDef.tabDefault;
    }
    const tabIds = resolveShellTabIds(shellDef);
    const firstAvailable = tabIds.find((id) => isAvailable(id));
    if (firstAvailable) {
        return firstAvailable;
    }
    if (requireAvailable) {
        return null;
    }
    // Hard fallback for bare-shell entry (startup / pre-login): never leave the URL tabless.
    if (explicitTabId && tabIds.includes(explicitTabId)) {
        return explicitTabId;
    }
    if (storedTabId && tabIds.includes(storedTabId)) {
        return storedTabId;
    }
    if (shellDef.tabDefault && tabIds.includes(shellDef.tabDefault)) {
        return shellDef.tabDefault;
    }
    return tabIds[0] ?? null;
}
